/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as PopupTable from '@/Components/Global/PopupTable/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Component } from '@/Constants/Component'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { BaseInput } from '@/Helpers/Components/DataForm/BaseInput'
import { InputButton } from '@/Helpers/Components/DataForm/InputButton'
import { InputDatePicker } from '@/Helpers/Components/DataForm/InputDatePicker'
import { InputEmail } from '@/Helpers/Components/DataForm/InputEmail'
import { InputNumber } from '@/Helpers/Components/DataForm/InputNumber'
import { InputLabel } from '@/Helpers/Components/DataForm/InputLabel'
import { InputPassword } from '@/Helpers/Components/DataForm/InputPassword'
import { InputPhone } from '@/Helpers/Components/DataForm/InputPhone'
import { InputSelect } from '@/Helpers/Components/DataForm/InputSelect'
import { InputText } from '@/Helpers/Components/DataForm/InputText'
import { InputTextArea } from '@/Helpers/Components/DataForm/InputTextArea'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @internal Interface @name DataFormButtonDefinition                                           **/
/**************************************************************************************************/
interface DataFormButtonDefinition
{
	key: string
	isFirst?: boolean
	isLast?: boolean
	text: {
		onInsert?: string | ((document: any) => string)
		onSelect?: string | ((document: any) => string)
		onUpdate?: string | ((document: any) => string)
	}
}

/**************************************************************************************************/
/** @internal Interface @name DataFormTabHeaderDefinition                                        **/
/**************************************************************************************************/
interface DataFormTabHeaderDefinition
{
	key: string
	text: DataFormButtonDefinition['text']
}

/**************************************************************************************************/
/** @internal Const @name BasicHeader                                                            **/
/**************************************************************************************************/
const DataForm = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'DataForm', mixins: [MixinBase, MixinComponent, MixinResponsive],

	components:
	{
		PopupTable: PopupTable.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			params: undefined as DataFormParamsDefinition | undefined,
			tabs: [] as Array<DataFormTabDefinition>,
			states:
			{
				action: Component.Actions.INSERT,
				document: undefined as any,
				showPopupTable: false,
				validateState: true
			}
		}
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		PopupTable() : PopupTable.PopupTableRef
		{
			return this.$refs.popupTable as PopupTable.PopupTableRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		AddOption(key: string, option: object) : void
		{
			const element = this.GetElement(key)
			if (element !== undefined && element instanceof InputSelect) element.Options.push(option)
		},

		CheckReadOnly(item: DataFormValidInputTypes) : boolean
		{
			// El Elemento puede ser deshabilitado si la propiedad 'readonly' esta presente.
			if (item.Params?.readonly) {
				return Array.isArray(item.Params.readonly)
					? item.Params.readonly.includes(this.states.action)
					: item.Params.readonly === this.states.action
			}
			return false
		},

		CheckRequired(item: DataFormValidInputTypes) : boolean
		{
			// El Elemento puede ser deshabilitado/habilitado si la propiedad 'enabled' esta presente
			// debido al llamado de la función setEnabled.
			if (item.Params?.enabled) {
				return !item.Params.enabled
			}

			// El Elemento puede ser deshabilitado si la propiedad 'readonly' esta presente.
			if (item.Params?.readonly) {
				return this.CheckReadOnly(item)
			}

			// El Elemento puede ser deshabilitado si la propiedad 'requires' esta presente.
			else if (item.Params?.requires) {
				const requiredField = item.Params.requires.field

				for (let n = 0; n < this.tabs.length; n++) {
					for (const element of this.tabs[n].columns!) {

						if (element.Key === requiredField) {
							// Si la propiedad 'values' esta presente, evaluar su valor,
							// de lo contrario, validar por defecto valores vacios/nulos.
							const values = item.Params.requires?.values
							if (values) {
								return !!this.ValidateValues(element as any, values)
							}
							else {
								if (element instanceof InputText)   return element.Value === ''
								if (element instanceof InputButton) return element.Data.value === ''
								if (element instanceof InputSelect) return element.Selected === null
							}
						}
					}
				}
			}
			return false
		},

		ClearInputs() : void
		{
			for (const tab of this.tabs)
			{
				for (const tabKey of ['columns', 'rows'])
				{
					if (tab?.[tabKey] !== undefined)
					{
						for (const element of tab[tabKey] as Array<DataFormValidInputTypes>)
						{
							if (element instanceof InputButton)     element.Data.value = ''
							if (element instanceof BaseInput)       element.Value    = ''
							if (element instanceof InputDatePicker) element.Value    = ''
							if (element instanceof InputSelect)     element.Selected = null
						}
					}
				}
			}
		},

		ClearOptions(key: string) : void
		{
			const element = this.GetElement(key)
			if (element !== undefined && element instanceof InputSelect) element.Options = []
		},

		GetColumn(index: number) : Array<DataFormValidInputTypes>
		{
			const currentTab = this.GetCurrentObjectTab()
			if (currentTab) {
				if (currentTab?.columns) {
					// Los elementos deben ser filtrados antes de decidir a cual columna corresponden.
					const auxItems = currentTab.columns.filter((item) => {
						if ('Params' in item && item.Params?.visible) {
							const vFound = item.Params?.visible.find((x) => x === this.states.action)
							return vFound !== undefined
						}
						else {
							return true
						}
					})

					// El ultimo filtro corresponde al 'index' de la columna.
					const items: Array<DataFormValidInputTypes> = []
					for (let step = 0; step < auxItems.length; step++) {
						if (step % this.params!.numColumns === index) {
							const item = auxItems[step]
							items.push(item as any)
						}
					}

					// Retornar el Array con los items.
					return items
				}
			}
			return []
		},

		GetCurrentObjectTab()
		{
			if (this.tabs.length > 0)
			{
				for (let tab of this.tabs)
				{
					if (this.GetCurrentTab() === tab.header.key)
					{
						return tab
					}
				}
			}
		},

		GetCurrentTab() : string | undefined
		{
			return this.params?.currentTab
		},

		GetDisabledClass(state: boolean) : string
		{
			return state ? 'disabled' : ''
		},

		GetElement<T>(key: string) : T | undefined
		{
			const { currentTab } = this.params!
			for (const tab of this.tabs)
			{
				if (tab.header.key === currentTab)
				{
					for (const tabKey of ['columns', 'rows'])
					{
						if (tab?.[tabKey] !== undefined)
						{
							for (const element of tab[tabKey] as Array<DataFormValidInputTypes>)
							{
								if (element.Key.toLowerCase() === key.toLowerCase()) return element as T
							}
						}
					}
					break
				}
			}
		},

		GetHeaderButtonClass(button: DataFormButtonDefinition) : string
		{
			let cls = ''
			if (this.GetCurrentTab() === button.key) cls += 'button-active '
			if (button?.isFirst === true) cls += 'button-is-first '
			if (button?.isLast === true) cls += 'button-is-last '
			return cls.trim()
		},

		GetHeaderButtons() : Array<DataFormButtonDefinition>
		{
			if (this.tabs.length > 0)
			{
				const buttons: Array<DataFormButtonDefinition> = []
				for (const tab of this.tabs) buttons.push(tab.header)
				return buttons
			}
			return []
		},

		GetHeaderButtonStyle() : string
		{
			const { length } = this.GetHeaderButtons()
			return `width: calc(100% / ${ length });`
		},

		GetHeaderButtonText(button: DataFormButtonDefinition) : string
		{
			switch (this.states.action)
			{
				case Component.Actions.INSERT: return typeof button.text.onInsert === 'function' ? button.text.onInsert(this.states.document) : button.text.onInsert as string
				case Component.Actions.READ  : return typeof button.text.onSelect === 'function' ? button.text.onSelect(this.states.document) : button.text.onSelect as string
				case Component.Actions.UPDATE: return typeof button.text.onUpdate === 'function' ? button.text.onUpdate(this.states.document) : button.text.onUpdate as string
			}
		},

		GetHeaderElement(key: string)
		{
			const { currentTab } = this.params!
			for (const tab of this.tabs)
			{
				if (tab.header.key === currentTab)
				{
					if (tab.header !== undefined && tab.header.key === key) return tab.header
				}
			}
		},

		GetInputButtonClass(item: DataFormValidInputTypes) : string
		{
			let classString = !this.CheckRequired(item) ? 'btn-green' : 'btn-gray'
			if (item.Params?.button)
			{
				if (item.Params.button.content === 'icon') classString += ' has-icon'
				else if (item.Params.button.content === 'text') classString += ' has-text'
			}
			return classString
		},

		GetInputFormatter(item: DataFormValidInputTypes) : any
		{
			return item.Params?.formatter ? item.Params.formatter : undefined
		},

		GetInputPasswordVisibilityIcon(item: InputPassword) : string
		{
			return item.Visibility ? 'eye' : 'eye-slash'
		},

		GetInputState(item: DataFormValidInputTypes, stateFunctionName = 'state') : any
		{
			if (item?.Params && (item.Params as any)?.[stateFunctionName]) {
				return (item.Params as any)[stateFunctionName](item)
			}
			return null
		},

		GetInputType(item: DataFormValidInputTypes) : string | undefined
		{
			switch (item.Params.type)
			{
				case 'phone':
				{
					return 'text'
				}
				default:
				{
					if (item instanceof InputPassword && item.Visibility) return 'text'
					return item.Params.type
				}
			}
		},

		GetNumColumns() : number
		{
			return this.params?.numColumns || 0
		},

		GetPlaceHolder(item: DataFormValidInputTypes, state = false) : string
		{
			if (state)
			{
				if (item.Params?.requires && item.Params.requires?.friendly)
				{
					return `Debe seleccionar el campo '${ item.Params.requires.friendly }' primero...`
				}
			}
			return item.Params.placeholder || ''
		},

		GetRows()
		{
			const tab = this.GetCurrentObjectTab()
			if (tab !== undefined)
			{
				if (tab?.rows)
				{
					// Segun el indice de la columna, insertar los items correspondientes.
					const items = []
					for (let i = 0; i < tab.rows.length; i++)
					{
						const item = tab.rows[i]
						if (item !== undefined)
						{
							if ('Params' in item && item.Params?.visible)
							{
								const vFound = item.Params?.visible.find((x) => x === this.states.action)
								if (vFound !== undefined) items.push(item)
							}
							else items.push(item)
						}
					}
					return items
				}
			}
			return []
		},

		GetSelectFirstOption(item: DataFormValidInputTypes) : string
		{
			if (item.Params?.placeholder) return item.Params.placeholder
			return '- Seleccione una Opción -'
		},

		GetValue<T>(key: string, dataProperty = '') : T | undefined
		{
			const element = this.GetElement<DataFormValidInputTypes>(key)
			if (element)
			{
				switch (element.Type)
				{
					case 'BaseInput':
					{
						const e = element as BaseInput
						return (typeof e.Value === 'number') ? e.Value : e.Value?.trim() as T
					}
					case 'InputButton':
					{
						const e = element as InputButton
						return (e.Data as any)[dataProperty]?.trim() as T
					}
					case 'InputDatePicker':
					{
						const e = element as InputDatePicker
						return e.Value as T
					}
					case 'InputSelect':
					{
						const e = element as InputSelect
						return e.Selected?.trim() as T
					}
				}
			}
		},

		Initialize(dataForm: { params: any, tabs: any }) : void
		{
			this.params = dataForm.params
			this.tabs = dataForm.tabs
		},

		InitPopupTable(fields: Array<any>, items: Array<any>) : void
		{
			const { DataTable } = this.PopupTable
			DataTable.SetElements(fields, items)
		},

		IsOptional(item: DataFormValidInputTypes) : boolean
		{
			if (item.Params?.optional) return item.Params.optional
			return false
		},

		OnDFCurrentSearchPaginationChanged(page: number, searchParams: any) : void
		{
			PrintHelper.warning('[DataForm]:onDFCurrentSearchPaginationChanged event triggered')
			this.$emit('onDFCurrentSearchPaginationChanged', page, searchParams)
		},

		OnDFPaginationChanged(page: number) : void
		{
			PrintHelper.warning('[DataForm]:onDFPaginationChanged event triggered')
			this.$emit('onDFPaginationChanged', page)
		},

		OnDFRefreshButtonClick() : void
		{
			PrintHelper.warning('[DataForm]:onDFRefreshButtonClick event triggered')
			this.$emit('onDFRefreshButtonClick')
		},

		OnDFSearchButtonClicked(searchParams: any) : void
		{
			PrintHelper.warning('[DataForm]:onDFSearchButtonClicked event triggered')
			this.$emit('onDFSearchButtonClicked', searchParams)
		},

		OnInputButtonClick(key: string, event: Event) : void
		{
			PrintHelper.warning('[DataForm]:onInputButtonClick event triggered')
			this.$emit('onInputButtonClick', key, event)
		},

		OnOptionChange(key: string, event: Event) : void
		{
			PrintHelper.warning('[DataForm]:onOptionChange event triggered')
			this.$emit('onOptionChange', key, event)
		},

		OnPTClose() : void
		{
			PrintHelper.warning('[DataForm]:onPTClose event triggered')
			this.Set<DataFormRef['states']>({ showPopupTable: false })
		},

		OnPTSelect(currentRow: object) : void
		{
			PrintHelper.warning('[DataForm]:onPTSelect event triggered')
			if (currentRow !== undefined)
			{
				this.Set<DataFormRef['states']>({ showPopupTable: false })
				this.$emit('onPTSelect', currentRow)
			}
		},

		OnRowDoubleClick(clickedRow: object) : void
		{
			PrintHelper.warning('[DataForm]:onRowDoubleClick():Event triggered')
			this.Set<DataFormRef['states']>({ showPopupTable: false })
			this.$emit('onRowDoubleClick', clickedRow)
		},

		OnSetCurrentTab(tabName: string) : void
		{
			if (this.GetCurrentTab() !== tabName) this.params!.currentTab = tabName
		},

		ResolveEventHandlers(item: BaseInput, nativeEvent: any) : any
		{
			if (item?.Events) return item.Events?.[nativeEvent.type] ? item.Events.keyup(item, nativeEvent) : null
			return null
		},

		SetEnabled(key: string, state: boolean) : void
		{
			const element = this.GetElement(key)
			if (element !== undefined && element instanceof InputButton)
			{
				element.Params.enabled = state
			}
		},

		SetValue(key: string, value: any, dataProperty = '') : void
		{
			const element = this.GetElement(key)
			if (element !== undefined)
			{
				if (element instanceof InputButton)     (element.Data as any)[dataProperty] = value
				if (element instanceof BaseInput)       element.Value    = value
				if (element instanceof InputDatePicker) element.Value    = value
				if (element instanceof InputSelect)     element.Selected = value
			}
		},

		ValidateStates() : boolean
		{
			for (let col = 0; col < this.GetNumColumns(); col++)
			{
				for (const $item of this.GetColumn(col))
				{
					if ($item?.Params && $item.Params?.state)
					{
						if ($item.Params.state($item) === false) return false
					}
				}
			}
			return true
		},

		ValidateValues(item: DataFormValidInputTypes, values: Array<any>) : boolean
		{
			// 'values' puede ser de tipo String o Array.
			if (Array.isArray(values))
			{
				if (item instanceof InputButton) return !(values.includes(item.Data.value))
				if (item instanceof BaseInput)   return !(values.includes(item.Value))
				if (item instanceof InputSelect) return !(values.includes(item.Selected))
			}
			else if (typeof values === 'string')
			{
				if (item instanceof InputButton) return !(item.Data.value === values)
				if (item instanceof BaseInput)   return !(item.Value === values)
				if (item instanceof InputSelect) return !(item.Selected === values)
			}
			return false
		}
	}
})

/**************************************************************************************************/
/** @export Component @name DataForm                                                             **/
/**************************************************************************************************/
export default DataForm

/**************************************************************************************************/
/** @export Type @name DataFormRef                                                               **/
/**************************************************************************************************/
export type DataFormRef = InstanceType<typeof DataForm>

/**************************************************************************************************/
/** @export Interface @name DataFormParamsDefinition                                             **/
/**************************************************************************************************/
export interface DataFormParamsDefinition
{
	currentTab: string
	numColumns: number
}

/**************************************************************************************************/
/** @export Interface @name DataFormTabDefinition                                                **/
/**************************************************************************************************/
export interface DataFormTabDefinition
{
	[key: string]: DataFormTabHeaderDefinition | Array<DataFormValidInputTypes> | undefined
	header: DataFormTabHeaderDefinition
	columns?: Array<DataFormValidInputTypes>
	rows?: Array<DataFormValidInputTypes>
}

/**************************************************************************************************/
/** @export Type @name DataFormValidInputTypes                                                   **/
/**************************************************************************************************/
export type DataFormValidInputTypes =
(
	InputDatePicker   |
	InputButton       |
	InputEmail        |
	InputLabel        |
	InputNumber       |
	InputPassword     |
	InputPhone        |
	InputSelect       |
	InputText         |
	InputTextArea
)